import api from "@/api";
import type { ApiResponse, UpdateCardSaveProps, PaymentMethodDefinitionsResponse, ChargeRequest, ChargeResponse } from "@/interfaces";
import { apiCache, createCacheKey } from "@/lib/api-cache";
import { addSavedPaymentMethodId, getSavedPaymentMethodIds } from "@/lib/saved-payment-methods-storage";
import { getDecodedTokenFromSource } from "@/lib/token-utils";
import { sendAnalyticsEvent, type SuperProperties } from "@/services/AnalyticsService";
import gameConfig from "../../game_config.json";

// Consistent cache key prefix for user payment methods (used for invalidation)
const USER_PAYMENT_METHODS_CACHE_PREFIX = '/user-payment-methods';

export async function getCheckoutSession(sessionId: string) {
  const cacheKey = createCacheKey('/checkout-session', { sessionId });
  
  return apiCache.get(
    cacheKey,
    async () => {
      try {
        const response = await api.get(`/checkout-session/${sessionId}`);
        return response;
      } catch (error) {
        console.error("Error fetching checkout session:", error);
        throw error;
      }
    },
    2 * 60 * 1000 // Cache for 2 minutes (checkout sessions are relatively stable)
  );
}

export async function userPaymentMethods() {
  // Get stored payment method IDs from localStorage for client-saved payment methods feature
  const decodedToken = getDecodedTokenFromSource('any');
  const appId = decodedToken?.appId;
  const customerId = decodedToken?.stripeCustomerId as string | undefined;
  
  // Build the query string with stored payment method IDs if available
  let queryString = '';
  if (appId && customerId) {
    const storedIds = getSavedPaymentMethodIds(appId, customerId);
    if (storedIds.length > 0) {
      queryString = `?payment_method_ids=${storedIds.join(',')}`;
    }
  }
  
  // Include queryString in cache key so different filter combinations are cached separately
  const cacheKey = createCacheKey(USER_PAYMENT_METHODS_CACHE_PREFIX, { queryString });
  
  return apiCache.get(
    cacheKey,
    async () => {
      const response = (await api.get(`${USER_PAYMENT_METHODS_CACHE_PREFIX}${queryString}`)) as ApiResponse;
      const filteredPaymentMethods = response.payment_methods.filter(
        (item) => item.type === "credit_card" || item.type === "paypal"
      );
      
      return filteredPaymentMethods;
    },
    1 * 60 * 1000 // Cache for 1 minute (payment methods can change more frequently)
  );
}

export async function updateCardSave({
  saveCard,
  sessionId,
}: UpdateCardSaveProps) {
  const response = await api.post(`/update-card-save`, {
    payment_intent_id: sessionId,
    should_save_card: saveCard,
  });
  
  // Invalidate ALL user payment methods cache entries (including those with query params)
  apiCache.deleteByPrefix(USER_PAYMENT_METHODS_CACHE_PREFIX);
  
  return response;
}

export async function getCustomerPortalUrl(): Promise<{ url: string }> {
  try {
    // Include current URL as return_url parameter to preserve checkout state
    const currentUrl = window.location.href;
    
    // Use the main API configuration instead of hardcoded localhost
    const response = await api.get(`/customer-portal?return_url=${encodeURIComponent(currentUrl)}`) as { url: string };
    
    return response;
  } catch (error) {
    console.error("❌ Error fetching customer portal URL:", error);
    
    // Log more details about the error
    if (error && typeof error === 'object') {
      console.log('📋 Error details:', {
        message: (error as any).message,
        status: (error as any).response?.status,
        statusText: (error as any).response?.statusText,
        data: (error as any).response?.data,
        config: {
          url: (error as any).config?.url,
          method: (error as any).config?.method,
          baseURL: (error as any).config?.baseURL,
          headers: (error as any).config?.headers
        }
      });
      
    }
    
    throw error;
  }
}

/**
 * Fetches available payment method definitions based on currency and country.
 * This determines which payment methods (like Cash App Pay) should be shown in the UI
 * based on what's enabled in the merchant's Stripe account.
 */
export async function getPaymentMethodDefinitions(
  currency?: string,
  country?: string
): Promise<PaymentMethodDefinitionsResponse> {
  const cacheKey = createCacheKey('/payment-method-definitions', { currency, country });
  
  return apiCache.get(
    cacheKey,
    async () => {
      try {
        const params = new URLSearchParams();
        if (currency) params.append('currency', currency);
        if (country) params.append('country', country);
        
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await api.get(`/payment-method-definitions${queryString}`) as PaymentMethodDefinitionsResponse;
        
        return response;
      } catch (error) {
        console.error("Error fetching payment method definitions:", error);
        // Return empty definitions on error to prevent UI breakage
        return { definitions: [] };
      }
    },
    2 * 60 * 1000 // Cache for 2 minutes (payment method configs are relatively stable)
  );
}

export async function processPayment(chargeRequest: ChargeRequest): Promise<ChargeResponse> {
  try {
    const response = await api.post('/charge', chargeRequest) as ChargeResponse;
    
    // Invalidate relevant caches after successful payment
    if (response.result.is_success) {
      // Invalidate ALL user payment methods cache entries (including those with query params)
      apiCache.deleteByPrefix(USER_PAYMENT_METHODS_CACHE_PREFIX);
      
      // Store the saved payment method ID in localStorage for client-saved payment methods feature
      if (chargeRequest.save_payment_method && response.result.saved_payment_method_id) {
        const decodedToken = getDecodedTokenFromSource('any');
        if (decodedToken?.appId && decodedToken?.stripeCustomerId) {
          addSavedPaymentMethodId(
            decodedToken.appId,
            decodedToken.stripeCustomerId as string,
            response.result.saved_payment_method_id
          );
          
          // Track payment_method_updated event
          try {
            const gameData = gameConfig[decodedToken.appId as keyof typeof gameConfig] as { analytics_webhook_url?: string } | undefined;
            const webhookUrl = gameData?.analytics_webhook_url;
            const params = new URLSearchParams(window.location.search);
            const superProperties: SuperProperties = {
              checkout_id: params.get('analytics_checkout_id') || undefined,
              distinct_id: params.get('analytics_distinct_id') || undefined,
              transaction_id: params.get('analytics_transaction_id') || undefined,
            };
            sendAnalyticsEvent(webhookUrl, 'payment_method_updated', superProperties, {});
          } catch {
            // Ignore analytics errors - never block payment flow
          }
        }
      }
    }
    
    return response;
  } catch (error) {
    console.error("Error processing payment:", error);
    throw error;
  }
}

export async function removePaymentMethod(paymentMethodId: string): Promise<void> {
  try {
    await api.post('/remove-payment-method', {
      payment_method_id: paymentMethodId,
    });
    
    // Invalidate ALL user payment methods cache entries (including those with query params)
    apiCache.deleteByPrefix(USER_PAYMENT_METHODS_CACHE_PREFIX);
  } catch (error) {
    console.error("Error removing payment method:", error);
    throw error;
  }
}